/** Pure view-models for the dashboard drill-down: plants (macro) -> plant by area -> area by equipment (micro). */
import type { PlantsOverview, ScorePoint } from '../../api/types'
import { filtersToSearch, type DashboardFilters, type KpiModel } from './models'

/** Moving down (or up) a level keeps the date range; the area filter belongs to one level only. */
function withDates(path: string, f: DashboardFilters): string {
  const search = filtersToSearch({ dateFrom: f.dateFrom, dateTo: f.dateTo }).toString()
  return search ? `${path}?${search}` : path
}

export const overviewPath = (f: DashboardFilters) => withDates('/', f)
export const plantPath = (plant: number, f: DashboardFilters) => withDates(`/plants/${plant}`, f)
export const areaPath = (plant: number, area: number, f: DashboardFilters) => withDates(`/plants/${plant}/areas/${area}`, f)

/** Lists reached from a plant dashboard must keep that plant as their hard scope. */
export function plantAssetsPath(plant: number, f: DashboardFilters, band?: DashboardFilters['band']): string {
  const search = filtersToSearch({ dateFrom: f.dateFrom, dateTo: f.dateTo, band }).toString()
  const params = new URLSearchParams(search)
  params.set('plant', String(plant))
  return `/assets?${params.toString()}`
}

export const plantWorkOrdersPath = (plant: number) => `/work-orders?plant=${plant}`

export interface PlantKpi extends Omit<KpiModel, 'key'> {
  key: 'plants' | 'equipment' | 'inspected' | 'critical' | 'work_orders'
}

function share(part: number, total: number): string {
  return `${total ? Math.round((100 * part) / total) : 0}% del total`
}

export function plantKpis(t: PlantsOverview['totals']): PlantKpi[] {
  return [
    { key: 'plants', label: 'Plantas', value: String(t.plants), caption: `${t.plants === 1 ? 'planta' : 'plantas'} registradas`, tone: 'brand' },
    { key: 'equipment', label: 'Total de equipos', value: String(t.equipment), caption: 'en todas las plantas', tone: 'blue' },
    { key: 'inspected', label: 'Con inspección', value: String(t.inspected), caption: share(t.inspected, t.equipment), tone: 'green' },
    { key: 'critical', label: 'Críticos', value: String(t.critical), caption: share(t.critical, t.equipment), tone: 'pink', band: 'critical' },
    { key: 'work_orders', label: 'Órdenes de trabajo abiertas', value: String(t.open_work_orders), caption: 'en todas las plantas', tone: 'peri' },
  ]
}

export interface ScoreHistoryModel {
  points: { date: string; total: number; band: string }[]
  delta: number | null
  deltaLabel: string
  summary: string
}

/** Score history of one equipment (oldest first). A higher score is worse, so a positive change is a deterioration. */
export function scoreHistory(history: ScorePoint[] | undefined): ScoreHistoryModel {
  const points = (history ?? []).map((p) => ({ date: p.inspected_on, total: Number(p.total), band: p.band }))
  const n = points.length
  const delta = n >= 2 ? Math.round((points[n - 1].total - points[n - 2].total) * 10) / 10 : null
  const deltaLabel = delta === null ? '—' : delta > 0 ? `+${delta.toFixed(1)}` : delta < 0 ? `−${Math.abs(delta).toFixed(1)}` : '0.0'
  const summary = n === 0 ? 'Sin historial de puntaje'
    : n === 1 ? '1 inspección'
      : `${n} inspecciones; cambio ${deltaLabel} desde la anterior`
  return { points, delta, deltaLabel, summary }
}
